<template>
  <el-form ref="form" :model="localData" :rules="rules" label-width="160px" size="small">
    <el-divider>行业资质</el-divider>

    <!-- 主体类型选择（个体户/企业） -->
    <el-form-item label="主体类型" prop="subjectType">
      <el-select v-model="subjectType" placeholder="请选择主体类型" style="width:100%" @change="handleSubjectTypeChange">
        <el-option label="个体工商户" value="INDIVIDUAL" />
        <el-option label="企业" value="ENTERPRISE" />
      </el-select>
    </el-form-item>

    <el-form-item label="行业类目" prop="industryCategory">
      <el-select v-model="localData.industryCategory" placeholder="请选择行业类目" style="width:100%" filterable @change="handleIndustryChange">
        <el-option v-for="item in industryOptions" :key="item.industryName" :label="item.industryName" :value="item.industryName" />
      </el-select>
    </el-form-item>

    <el-form-item label="结算规则ID" prop="settlementId">
      <el-select v-model="localData.settlementId" placeholder="请选择结算规则" style="width:100%" disabled>
        <el-option v-for="item in settlementOptions" :key="item.settlementId" :label="`${item.settlementId} - ${item.feeRate} (${item.cycle})`" :value="item.settlementId" />
      </el-select>
    </el-form-item>

    <el-form-item label="标准费率" prop="standardRate">
      <div class="rate-input">
        <el-input-number v-model="rateValue" :min="0" :max="100" :precision="2" :step="0.1" placeholder="请输入费率" style="width:200px" @change="handleRateChange" />
        <span class="rate-unit">%</span>
        <span class="rate-tip">（如：0.6 表示 0.6%）</span>
      </div>
    </el-form-item>

    <el-form-item label="优惠费率">
      <el-radio-group v-model="localData.hasPromotion">
        <el-radio :label="0">否</el-radio>
        <el-radio :label="1">是</el-radio>
      </el-radio-group>
    </el-form-item>

    <template v-if="localData.hasPromotion === 1">
      <el-form-item label="优惠费率ID" prop="promotionId">
        <el-input v-model="localData.promotionId" placeholder="请输入优惠费率ID" />
      </el-form-item>
      <el-form-item label="借记卡费率">
        <div class="rate-input">
          <el-input-number v-model="debitRateValue" :min="0" :max="100" :precision="2" :step="0.1" style="width:200px" @change="handleDebitRateChange" />
          <span class="rate-unit">%</span>
        </div>
      </el-form-item>
      <el-form-item label="贷记卡费率">
        <div class="rate-input">
          <el-input-number v-model="creditRateValue" :min="0" :max="100" :precision="2" :step="0.1" style="width:200px" @change="handleCreditRateChange" />
          <span class="rate-unit">%</span>
        </div>
      </el-form-item>
    </template>

    <el-divider>特殊资质</el-divider>

    <el-alert
        v-if="currentRule && currentRule.needQualification"
        title="该行业需要特殊资质"
        type="warning"
        :description="currentRule.qualification"
        show-icon
        :closable="false"
        style="margin-bottom: 16px"
    />

    <el-form-item label="资质图片">
      <div class="upload-row">
        <el-input v-model="localData.qualificationUrls" placeholder="请点击选择图片按钮选择图片" readonly />
        <el-button type="primary" @click="openImageSelector('qualification')">选择图片</el-button>
      </div>
      <div v-if="localData.qualificationUrls" class="image-preview">
        <el-image :src="localData.qualificationUrls" class="thumb" fit="cover" />
      </div>
      <div v-if="localData.qualificationMediaId" class="media-id-info">
        <span class="media-id-label">微信MediaId:</span>
        <span class="media-id-value">{{ localData.qualificationMediaId }}</span>
      </div>
    </el-form-item>

    <div class="module-footer">
      <el-button type="primary" @click="submitModule" :loading="saving">保 存</el-button>
    </div>

    <!-- 图片选择器 -->
    <image-selector
        v-model="showImageSelector"
        :max-count="1"
        @select="handleImageSelect"
    />
  </el-form>
</template>

<script>
import api from '@/utils/api'
import ImageSelector from '@/components/ImageSelector'
import { getIndustryListBySubjectType, getRuleByIndustry, getDefaultSettlementId } from '@/utils/settlementRules'

export default {
  name: 'IndustryInfo',
  components: { ImageSelector },
  props: {
    data: { type: Object, default: () => ({}) },
    applyId: { type: Number, default: null },
    applyNo: { type: String, default: '' },
    merchantId: { type: Number, default: null }
  },
  data() {
    return {
      saving: false,
      showImageSelector: false,
      currentUploadField: '',
      rateValue: 0,
      debitRateValue: 0,
      creditRateValue: 0,
      subjectType: 'ENTERPRISE',
      industryOptions: [],
      settlementOptions: [],
      localData: {
        industryCategory: '',
        settlementId: '',
        standardRate: '',
        hasPromotion: 0,
        promotionId: '',
        promotionRate: '',
        debitRate: '',
        creditRate: '',
        qualificationUrls: '',
        qualificationImageId: null,
        qualificationMediaId: '',
        additionDesc: ''
      },
      rules: {
        industryCategory: [{ required: true, message: '请选择行业类目', trigger: 'change' }],
        settlementId: [{ required: true, message: '请选择结算规则', trigger: 'change' }],
        standardRate: [{ required: true, message: '请输入标准费率', trigger: 'blur' }]
      }
    }
  },
  computed: {
    currentRule() {
      if (!this.localData.industryCategory || !this.subjectType) return null
      return getRuleByIndustry(this.localData.industryCategory, this.subjectType)
    }
  },
  watch: {
    data: {
      immediate: true,
      handler(val) {
        if (val && Object.keys(val).length > 0) {
          this.localData = {
            industryCategory: val.industryCategory !== undefined ? val.industryCategory : this.localData.industryCategory,
            settlementId: val.settlementId !== undefined ? val.settlementId : this.localData.settlementId,
            standardRate: val.standardRate !== undefined ? val.standardRate : this.localData.standardRate,
            hasPromotion: val.hasPromotion !== undefined ? val.hasPromotion : this.localData.hasPromotion,
            promotionId: val.promotionId !== undefined ? val.promotionId : this.localData.promotionId,
            promotionRate: val.promotionRate !== undefined ? val.promotionRate : this.localData.promotionRate,
            debitRate: val.debitRate !== undefined ? val.debitRate : this.localData.debitRate,
            creditRate: val.creditRate !== undefined ? val.creditRate : this.localData.creditRate,
            qualificationUrls: val.qualificationUrls !== undefined ? val.qualificationUrls : this.localData.qualificationUrls,
            qualificationImageId: val.qualificationImageId !== undefined ? val.qualificationImageId : this.localData.qualificationImageId,
            qualificationMediaId: val.qualificationMediaId !== undefined ? val.qualificationMediaId : this.localData.qualificationMediaId,
            additionDesc: val.additionDesc !== undefined ? val.additionDesc : this.localData.additionDesc
          }
          if (val.standardRate) this.rateValue = parseFloat(val.standardRate)
          if (val.debitRate) this.debitRateValue = parseFloat(val.debitRate)
          if (val.creditRate) this.creditRateValue = parseFloat(val.creditRate)
        }
      }
    }
  },
  created() {
    this.loadIndustryList()
  },
  methods: {
    // 加载行业列表
    loadIndustryList() {
      this.industryOptions = getIndustryListBySubjectType(this.subjectType)
    },

    // 主体类型变更
    handleSubjectTypeChange() {
      this.localData.industryCategory = ''
      this.localData.settlementId = ''
      this.settlementOptions = []
      this.loadIndustryList()
    },

    // 行业变更
    handleIndustryChange(industryName) {
      const rule = getRuleByIndustry(industryName, this.subjectType)
      if (rule) {
        this.localData.settlementId = rule.settlementId
        this.settlementOptions = [{
          settlementId: rule.settlementId,
          feeRate: rule.feeRate,
          cycle: rule.cycle
        }]
        // 自动填充费率
        const rateStr = rule.feeRate.replace('%', '')
        const rate = parseFloat(rateStr)
        if (!isNaN(rate)) {
          this.localData.standardRate = (rate / 100).toFixed(4)
          this.rateValue = rate
        }
      }
    },

    openImageSelector(field) {
      this.currentUploadField = field
      this.showImageSelector = true
    },

    async handleImageSelect(images) {
      if (!images || images.length === 0) return
      const image = images[0]
      const imageUrl = image.url
      const imageId = image.id

      const urlField = this.getUrlField(this.currentUploadField)
      const idField = this.getIdField(this.currentUploadField)
      const mediaIdField = this.getMediaIdField(this.currentUploadField)

      if (urlField) this.$set(this.localData, urlField, imageUrl)
      if (idField) this.$set(this.localData, idField, imageId)

      const purpose = this.getPurpose(this.currentUploadField)
      if (purpose && imageUrl) {
        try {
          const res = await api.subMerchant_fetchMediaId({ imageUrl, purpose })
          if (res?.data?.code === 200 || res?.data?.success) {
            const data = res.data.data
            const mediaId = data?.media_id || data?.mediaId
            if (mediaId && mediaIdField) {
              this.$set(this.localData, mediaIdField, mediaId)
            }
          }
        } catch (e) {
          console.error('获取微信MediaId失败:', e)
        }
      }
    },

    getUrlField(field) { return { 'qualification': 'qualificationUrls' }[field] || '' },
    getIdField(field) { return { 'qualification': 'qualificationImageId' }[field] || '' },
    getMediaIdField(field) { return { 'qualification': 'qualificationMediaId' }[field] || '' },
    getPurpose(field) { return { 'qualification': 'business_addition_pics' }[field] || '' },

    validate(cb) {
      if (this.$refs.form) this.$refs.form.validate(cb)
      else cb(false)
    },

    handleRateChange(val) {
      this.localData.standardRate = val ? String(val) : ''
    },
    handleDebitRateChange(val) {
      this.localData.debitRate = val ? String(val) : ''
    },
    handleCreditRateChange(val) {
      this.localData.creditRate = val ? String(val) : ''
    },

    async submitModule() {
      this.$refs.form.validate(async (valid) => {
        if (!valid) return this.$message.warning('请完善必填信息')
        this.saving = true
        try {
          const params = {
            id: this.applyId,
            applyNo: this.applyNo,
            merchantId: this.merchantId,
            rejectReason: 'industry',
            industryCategory: this.localData.industryCategory,
            settlementId: this.localData.settlementId,
            standardRate: this.localData.standardRate,
            hasPromotion: this.localData.hasPromotion,
            promotionId: this.localData.promotionId,
            promotionRate: this.localData.promotionRate,
            debitRate: this.localData.debitRate,
            creditRate: this.localData.creditRate,
            qualificationUrls: this.localData.qualificationUrls,
            qualificationImageId: this.localData.qualificationImageId,
            qualificationMediaId: this.localData.qualificationMediaId,
            additionDesc: this.localData.additionDesc
          }
          console.log('========== [IndustryInfo] 提交行业资质 ==========')
          console.log('提交数据:', JSON.stringify(params, null, 2))
          const res = await api.subMerchant_saveApply(params)
          console.log('[IndustryInfo] 响应:', res)
          if (res?.data?.success) {
            this.$emit('saved', res.data.data)
          } else {
            this.$message.error(res?.data?.message || '保存失败')
          }
        } catch (e) {
          console.error('[IndustryInfo] 保存失败:', e)
          this.$message.error('保存失败')
        } finally {
          this.saving = false
        }
      })
    }
  }
}
</script>

<style scoped>
.upload-row { display: flex; gap: 8px; }
.upload-row .el-input { flex: 1; }
.image-preview { margin-top: 8px; }
.thumb { width: 100px; height: 100px; border: 1px solid #eee; border-radius: 4px; object-fit: cover; }
.media-id-info { margin-top: 8px; font-size: 12px; color: #606266; }
.media-id-label { font-weight: 500; color: #909399; }
.media-id-value { margin-left: 8px; color: #67c23a; word-break: break-all; }
.rate-input { display: flex; align-items: center; gap: 8px; }
.rate-unit { font-size: 14px; color: #606266; }
.rate-tip { font-size: 12px; color: #909399; }
.form-tip { font-size: 12px; color: #909399; margin-top: 4px; }
.module-footer { margin-top: 20px; padding-top: 16px; border-top: 1px solid #ebeef5; text-align: right; }
</style>